/**
 * useConversations — Custom hook for conversation CRUD and state.
 *
 * Layer: C (UX/CLI), adapted by B for integration with lib/api.ts
 * Spec: Uppdrag C §2.5, §1.1–1.4
 *
 * @decision Uses lib/api.ts conversations endpoint (auto-refresh, VITE_API_URL).
 * @decision Caches messages per conversation in a Map to avoid redundant fetches.
 * @decision Optimistic delete: removes from list immediately, rolls back on API error.
 *
 * B-INTEGRATION NOTES:
 *   - Original C version hardcoded `/api/conversations` — replaced with lib/api.ts.
 *   - Token no longer passed as prop — lib/api.ts handles auth headers internally.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  conversations as convApi,
  type Conversation,
  type Message,
} from '../lib/api';

// ─── Types ────────────────────────────────────────────────────────────

export interface ConversationItem {
  id: string;
  title: string | null;
  model: string;
  total_tokens: number;
  total_cost_usd: number;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageData {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  model?: string | null;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  created_at?: string;
}

interface UseConversationsReturn {
  conversations: ConversationItem[];
  activeId: string | null;
  isLoading: boolean;
  error: string | null;
  createConversation: (model: string, title?: string) => Promise<string>;
  deleteConversation: (id: string) => Promise<void>;
  selectConversation: (id: string) => void;
  loadMessages: (id: string) => Promise<ChatMessageData[]>;
  refresh: () => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────

function apiConvToItem(conv: Conversation): ConversationItem {
  return {
    id: conv.id,
    title: conv.title,
    model: conv.model,
    total_tokens: conv.total_tokens,
    total_cost_usd: conv.total_cost_usd,
    created_at: conv.created_at,
    updated_at: conv.updated_at,
  };
}

function apiMsgToData(msg: Message): ChatMessageData {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    model: msg.model,
    tokens_in: msg.tokens_in ?? undefined,
    tokens_out: msg.tokens_out ?? undefined,
    cost_usd: msg.cost_usd ?? undefined,
    created_at: msg.created_at,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────

export function useConversations(): UseConversationsReturn {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Message cache: Map<conversationId, messages[]>
  const messageCacheRef = useRef<Map<string, ChatMessageData[]>>(new Map());

  // ── Fetch conversation list ──────────────────────────────────────────

  const fetchConversations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { conversations: list } = await convApi.list(50, 0);
      setConversations(list.map(apiConvToItem));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Okänt fel';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // ── Create ───────────────────────────────────────────────────────────

  const createConversation = useCallback(
    async (model: string, title?: string): Promise<string> => {
      setError(null);

      const created = await convApi.create(model, title ?? 'Ny konversation');

      const newConv: ConversationItem = {
        id: created.id,
        title: created.title ?? 'Ny konversation',
        model: created.model ?? model,
        total_tokens: 0,
        total_cost_usd: 0,
        created_at: created.created_at ?? new Date().toISOString(),
        updated_at: created.updated_at ?? new Date().toISOString(),
      };

      // Prepend to list (newest first)
      setConversations((prev) => [newConv, ...prev]);
      setActiveId(created.id);

      return created.id;
    },
    [],
  );

  // ── Delete (optimistic) ──────────────────────────────────────────────

  const deleteConversation = useCallback(
    async (id: string): Promise<void> => {
      setError(null);

      // Snapshot for rollback
      const snapshot = [...conversations];
      setConversations((prev) => prev.filter((c) => c.id !== id));

      // Clear active if deleting current
      if (activeId === id) {
        setActiveId(null);
      }

      // Clear message cache
      messageCacheRef.current.delete(id);

      try {
        await convApi.delete(id);
      } catch (err) {
        // Rollback on error
        setConversations(snapshot);
        const msg = err instanceof Error ? err.message : 'Radering misslyckades';
        setError(msg);
        throw err;
      }
    },
    [conversations, activeId],
  );

  // ── Select ───────────────────────────────────────────────────────────

  const selectConversation = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  // ── Load messages ────────────────────────────────────────────────────

  const loadMessages = useCallback(
    async (id: string): Promise<ChatMessageData[]> => {
      // Check cache first
      const cached = messageCacheRef.current.get(id);
      if (cached) return cached;

      const { messages: msgs } = await convApi.get(id);
      const data = msgs.map(apiMsgToData);

      // Cache
      messageCacheRef.current.set(id, data);

      return data;
    },
    [],
  );

  // ── Refresh ──────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    messageCacheRef.current.clear();
    await fetchConversations();
  }, [fetchConversations]);

  return {
    conversations,
    activeId,
    isLoading,
    error,
    createConversation,
    deleteConversation,
    selectConversation,
    loadMessages,
    refresh,
  };
}
